/**
 * Projéteis balísticos compartilhados (doc 06, Projéteis; doc 02 §12): posição exata por fórmula
 * fechada (sem integrar passo a passo), então cliente e servidor desenham e testam a mesma curva.
 * Só aritmética básica e `Math.sqrt` (determinismo, doc 07 A2).
 */
export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export interface Ballistic {
  /** Origem e velocidade inicial. */
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  /** Gravidade (m/s², para baixo). */
  gravity: number;
}

/** Posição `t` segundos depois do disparo. */
export function ballisticAt(b: Ballistic, t: number, out: Vec3 = { x: 0, y: 0, z: 0 }): Vec3 {
  out.x = b.x + b.vx * t;
  out.y = b.y + b.vy * t - 0.5 * b.gravity * t * t;
  out.z = b.z + b.vz * t;
  return out;
}

/** Velocidade `t` segundos depois do disparo (para orientar a flecha). */
export function ballisticVelocity(b: Ballistic, t: number): Vec3 {
  return { x: b.vx, y: b.vy - b.gravity * t, z: b.vz };
}

/** Velocidade inicial que leva de `from` a `to` em `seconds` (arremesso com tempo de voo fixo). */
export function aimBallistic(from: Vec3, to: Vec3, seconds: number, gravity: number): Vec3 {
  const t = Math.max(1e-3, seconds);
  return { x: (to.x - from.x) / t, y: (to.y - from.y + 0.5 * gravity * t * t) / t, z: (to.z - from.z) / t };
}

/** Altura da mão de quem arremessa (o caldeirão sai daqui), acima dos pés. */
export const LOB_RELEASE_HEIGHT = 1.6;

/**
 * Curva do arremesso de criatura: a velocidade que leva de `from` a `to` no tempo do voo. O cliente
 * desenha e o servidor testa a cúpula do Bastião (F96) na mesma curva.
 */
export function lobPath(from: Vec3, to: Vec3, seconds: number, gravity: number): Ballistic {
  const v = aimBallistic(from, to, seconds, gravity);
  return { x: from.x, y: from.y, z: from.z, vx: v.x, vy: v.y, vz: v.z, gravity };
}

/**
 * Primeiro contato do segmento `a → b` com o cilindro vertical (no plano e na faixa de altura):
 * fração do segmento em [0, 1], ou `undefined` se não toca.
 */
export function segmentCylinderEntry(a: Vec3, b: Vec3, c: { x: number; z: number; radius: number; bottom: number; top: number }): number | undefined {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const fx = a.x - c.x;
  const fz = a.z - c.z;
  const qa = dx * dx + dz * dz;
  const qb = 2 * (fx * dx + fz * dz);
  const qc = fx * fx + fz * fz - c.radius * c.radius;
  let t0 = 0;
  let t1 = 1;
  if (qa < 1e-12) {
    if (qc > 0) return undefined;
  } else {
    const disc = qb * qb - 4 * qa * qc;
    if (disc < 0) return undefined;
    const root = Math.sqrt(disc);
    t0 = Math.max(0, (-qb - root) / (2 * qa));
    t1 = Math.min(1, (-qb + root) / (2 * qa));
    if (t0 > t1) return undefined;
  }
  // Faixa de altura: y é linear no segmento.
  const dy = b.y - a.y;
  if (Math.abs(dy) < 1e-12) return a.y >= c.bottom && a.y <= c.top ? t0 : undefined;
  let y0 = (c.bottom - a.y) / dy;
  let y1 = (c.top - a.y) / dy;
  if (y0 > y1) [y0, y1] = [y1, y0];
  const start = Math.max(t0, y0);
  return start <= Math.min(t1, y1) ? start : undefined;
}

/**
 * Primeiro ponto do segmento abaixo do chão, em `steps` amostras (a varredura de cada subpasso tem
 * no máximo ~1 m): fração em [0, 1], ou `undefined`.
 */
export function terrainEntry(height: (x: number, z: number) => number, a: Vec3, b: Vec3, steps = 4): number | undefined {
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    if (a.y + (b.y - a.y) * t <= height(a.x + (b.x - a.x) * t, a.z + (b.z - a.z) * t)) return t;
  }
  return undefined;
}
